import { Navigate, Route, Routes } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useEffect } from "react";
import AdminLoginScreen from "./screens/auth/AdminLoginScreen";
import AccountsScreen from "./screens/accounts/AccountsScreen";
import ActivityLogsScreen from "./screens/activityLogs/ActivityLogsScreen";
import { AdminLayout } from "./components/layout/AdminLayout";
import { RouteNames } from "./navigation/RouteNames";
import { loadCurrentAdmin } from "./store/slices/adminAuthSlice";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import AppMessageModal from "./components/modals/AppMessageModal";

const ProtectedRoute = () => {
  const { token, initialized, user } = useAppSelector(
    (state) => state.adminAuth,
  );

  if (!initialized) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!token) {
    return <Navigate to={RouteNames.login} replace />;
  }

  if (user?.role !== "admin") {
    return <Navigate to={RouteNames.login} replace />;
  }

  return <AdminLayout />;
};

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { token } = useAppSelector((state) => state.adminAuth);

  if (token) {
    return <Navigate to={RouteNames.accounts} replace />;
  }

  return <>{children}</>;
};

const AppRoutes = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(loadCurrentAdmin());
  }, [dispatch]);

  return (
    <>
      <Routes>
        <Route
          path={RouteNames.login}
          element={
            <PublicRoute>
              <AdminLoginScreen />
            </PublicRoute>
          }
        />

        <Route element={<ProtectedRoute />}>
          <Route path={RouteNames.accounts} element={<AccountsScreen />} />
          <Route
            path={RouteNames.activityLogs}
            element={<ActivityLogsScreen />}
          />
        </Route>

        <Route
          path="*"
          element={<Navigate to={RouteNames.accounts} replace />}
        />
      </Routes>

      <AppMessageModal />
    </>
  );
};

export const App = () => {
  return <AppRoutes />;
};

export default App;
